import {useCallback} from 'react';
import {useStore} from 'react-redux';
import {useHistory, useParams} from 'react-router-dom';
import {sendEvarData} from '@ibibo/analytics';

import {getter, setter} from '@ibibo/client-side-storage';

import transformVoyagerResultToFilter from '../utils/transformVoyagerResultToFilter';
import {
  RESET_SRP_DATA,
  UPDATE_SRP_CONFIG,
  IUpdate_Srp_Config,
  IHotelData,
} from '../pages/SRP/redux/types';
import {RESET_DETAILS_DATA, IUpdate_Details_Config_Action} from '../pages/DetailsPage/redux/types';
import makeSrpUrl from '../utils/makeSrpUrl';
import makeDetailsPageUrl from '../utils/makeDetailsPageUrl';
import {fetchSrpDataThunk} from '../pages/SRP/redux/thunks';
import {resetFilterAction} from '../pages/SRP/redux/actions';
import {
  fetchDetailsStaticDataThunk,
} from '../pages/DetailsPage/redux/thunks';
import {updateDetailsPageConfigAction} from '../pages/DetailsPage/redux/actions';
import {RESET_OFFER_WIDGET} from '../pages/SRP/offer-widget-redux/types';
import {RESET_SEGMENT_SLIDER} from '../pages/common/redux/types';

export function useNavigateForSearchWidget() {
  const history = useHistory();
  const store = useStore();

  const resetCaller = useCallback(() => {
    store.dispatch({type: RESET_SRP_DATA});
    store.dispatch({type: RESET_OFFER_WIDGET});
    store.dispatch({type: RESET_SEGMENT_SLIDER});
    store.dispatch({type: RESET_DETAILS_DATA});
  }, []);

  const updateSrpFilterCaller = useCallback((actionData: IUpdate_Srp_Config['actionData']) => {
    store.dispatch({type: UPDATE_SRP_CONFIG, actionData});
  }, []);

  const fetchSrpDataThunkCaller = useCallback((arg: boolean) => {
    store.dispatch(fetchSrpDataThunk(arg));
  }, []);

  const fetchDetailsDataThunkCaller = useCallback((hotelId: string) => {
    store.dispatch(fetchDetailsStaticDataThunk(hotelId));
  }, []);

  const updateDetailsPageConfigCaller = useCallback(
    (config: IUpdate_Details_Config_Action['actionData']) => {
      store.dispatch(updateDetailsPageConfigAction(config));
    },
    [],
  );

  const onSearch = (
    openInNewTab = false,
    hotelData: IHotelData = null,
    newFilterData = {},
    resetAllData = true,
    exclusions = [],
    resetOldFilter = false
  ) => {
    const {srp, common, details} = store.getState();
    const {destination, checkin, checkout, roomString, partner, metaPartner, country} = common;
    const {srpConfig, srpData} = srp;

    const offerCode = srpConfig.filter?.pc;

    if (!destination || (!destination._id && !hotelData)) return;

    let filter = transformVoyagerResultToFilter(destination) || {};

    let cityIdFromDestination = '';
    const {t, ct = {}, _id = '', n = ''} = destination;

    if (ct && Object.keys(ct).length > 0) {
      cityIdFromDestination = ct._id || _id || '';
    }

    let hasNewFiltersToBeApplied = false;
    if (newFilterData && Object.keys(newFilterData).length) {
      hasNewFiltersToBeApplied = true;
    }

    const vcid = (srpData && srpData?.city_meta_info && srpData?.city_meta_info?.vcid) || '';

    if (
      (filter && srpData && srpData.city_meta_info && vcid !== cityIdFromDestination) ||
      resetOldFilter
    ) {
      srpConfig.filter = {};
    }

    filter = {...filter, ...srpConfig.filter};

    // if (partner === 'gostays' && exclusions && !exclusions.includes('gostays')) {
    //   filter.tag = 'goStays';
    // }

    if (!openInNewTab) {
      if (resetAllData) resetCaller();
      updateSrpFilterCaller({filter: {...filter, ...newFilterData}});
    }

    if (t === 'hotel' || (hotelData && hotelData.hn && !hasNewFiltersToBeApplied)) {
      sendEvarData({
        eVar12: "D=c12",
        prop12: "hotel search",
      })
      const {staticData = {}} = details;
      const detailsPageUrl = makeDetailsPageUrl({
        checkin,
        checkout,
        roomString,
        destination,
        partner,
        metaPartner,
        staticData,
        offerCode,
        appliedFilters: hotelData?.af,
        hotelData: hotelData
          ? hotelData
          : {
              hn: n,
              hc: _id,
            },
      });

      if (openInNewTab) {
        window.open(detailsPageUrl, '_blank');
      } else {
        // fetchDetailsDataThunkCaller(_id);
        updateDetailsPageConfigCaller({
          vhid: _id,
          roomString,
          // ibp,
          // metaPartner,
          filter: {},
          checkin,
          checkout,
          selectedHotelStatic: {
            hn: n,
            hc: _id,
            // ibp,
            // hmd,
          },
        } as IUpdate_Details_Config_Action['actionData']);
        history.push(detailsPageUrl);
      }
    } else {

      sendEvarData({
        eVar12: "D=c12",
        prop12: "city search",
      })

      const srpUrl = makeSrpUrl({
        checkin,
        checkout,
        destination,
        roomString,
        filter: {
          ...filter,
          ...newFilterData,
        },
        partner,
      });

      if (openInNewTab) {
        window.open(srpUrl, '_blank');
      } else {
        fetchSrpDataThunkCaller(false);
        history.push(srpUrl);
      }
    }

    const goHomeSearchesList = getter('go_home_searches');

    let searchesList = [];

    if (goHomeSearchesList && goHomeSearchesList.length) {
      searchesList = [...goHomeSearchesList];
    }

    let targetDestination = {...destination, t: destination.t};

    if ((!destination || !destination.n) && Object.keys(hotelData).length > 0) {
      targetDestination = {n: hotelData.hn, _id: hotelData.hc, t: 'hotel'};
    }

    for (let i = 0; i < searchesList.length; i++) {
      if (
        searchesList[i].destination.n === targetDestination.n &&
        new Date(searchesList[i].checkin.date).toDateString() === new Date(checkin.date).toDateString() &&
        new Date(searchesList[i].checkout.date).toDateString() === new Date(checkout.date).toDateString() &&
        searchesList[i].roomString === roomString
      ) {
        searchesList.splice(i, 1);
        break;
      }
    }
  
    if (
      !searchesList.filter(
        item =>
          item.destination.n === targetDestination.n &&
          new Date(item.checkin.date).toDateString() === new Date(checkin.date).toDateString() &&
          new Date(item.checkout.date).toDateString() === new Date(checkout.date).toDateString() &&
          item.roomString === roomString,
      ).length
    ) {
      searchesList.unshift({
        checkin: checkin,
        checkout: checkout,
        roomString: roomString,
        destination: targetDestination,
        partner: partner,
      });

      if (searchesList.length > 5) {
        searchesList.pop();
      }

      setter('go_home_searches', searchesList);
    }
  };

  return [onSearch];
}
